import React, { Suspense } from 'react';
import './index.css';
import { CMSColumns } from '@/components/cms/cms-columns';
import { cmsQueryKeys } from '@/services/cms/cmsQueryKeys';
import { getCMSPageContent } from '@/services/cms/getCMSPageContent';
import { skipToken, useQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';
import { CMSText } from '@/components/cms/cms-text';
import { CMSBanner } from '@/components/cms/cms-banner';
import { CMSTabs } from '@/components/cms/cms-tabs';
import { CMSTextAdvanced } from '@/components/cms/cms-text-advanced';
import { CMSCard } from '@/components/cms/cms-card';
import { CMSPlanCardsWrapper } from '@/components/cms/cms-plan-cards-wrapper';
import { cn } from '@/lib/utils';
import { usePage } from '@/hooks/use-page';
import { CMSCTAButton } from '../cms/cms-cta-button';
import { CMSImage } from '../cms/cms-image';
import { CMSPlaceholder } from '../cms/cms-placeholder';
import { FullScreenLoadingSpinner } from '../full-screen-loading-spinner';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { isValidJwtToken } from '@/utils/agent';
import { CMSSeo } from '../cms-seo';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorBoundaryFallback } from '../fallback';
import { getGlobalStore } from '@/utils/store';
import { pageContentQueryOptions } from '@/services/queryOptions/pageContentQueryOptions';

const LazyCMSVideo = React.lazy(() => import('../cms/cms-video'));

const CMSRender = () => {
  const pageKey = usePage();
  const navigate = useNavigate();
  const pathname = useLocation({
    select: (location) => location.pathname,
  });
  // Note: I tried using trailingslash = never in router config but it doesn't work
  // If pagekey is undefined that means routes are matched but it didn't match anything from our route-slug mapping(page.constant.ts) - it happens when path has trailing slash.
  // For example: /app/ - using navigate({ to: '.' }) removes the trailing slash. /app/ => /app\
  // Ignore blog pages
  React.useEffect(() => {
    if (pageKey === undefined && !pathname.includes('/blog')) {
      navigate({
        to: '.',
      });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pageKey, navigate, pathname]);

  const { agentToken } = useSearch({
    strict: false,
    select: (search) => ({
      agentToken: search['agent-token'],
    }),
  });
  const { setAgentLogout } = getGlobalStore();

  const { isPending, data, isError } = useQuery(
    pageContentQueryOptions(pageKey)
  );

  React.useEffect(() => {
    if (!agentToken) {
      return;
    }

    const isAgentTokenValid = agentToken && isValidJwtToken(agentToken);
    if (!isAgentTokenValid) {
      setAgentLogout();
      navigate({
        to: '.',
        // Remove agent-token from search params + keep the rest of the params
        search: (search) => ({
          ...search,
          'agent-token': undefined,
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isPending) {
    return (
      <div className="min-h-[95vh] flex items-center justify-center">
        <FullScreenLoadingSpinner />;
      </div>
    );
  }

  if (isError) {
    return <h1>Error</h1>;
  }
  return (
    <>
      <CMSSeo
        data={{
          seo_page_title: data.seo_page_title,
          seo_description: data.seo_description,
          seo_keywords: data.seo_keywords,
        }}
      />
      {/* Remove margin top if banner has previous sibling carousel */}
      <div className='[&_[data-component="CMSColumnsCarousel"]+[data-component="CMSBanner"]]:mt-0 md:[&_[data-component="CMSColumnsCarousel"]+[data-component="CMSBanner"]]:mt-0 lg:[&_[data-component="CMSColumnsCarousel"]+[data-component="CMSBanner"]]:mt-0'>
        {data.content.map((content, index) =>
          match(content)
            .with({ type: 'columns' } as const, (columnsData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSColumns
                  columnsData={columnsData}
                  // Remove padding for CMSCardVerticalCard in a carousel if it's not inside a banner.
                  // (e.g Ideal for working from home, Suitable for online gaming with no lag)
                  className={cn(
                    '[&[data-component="CMSColumnsStandard"]]:container [&[data-component="CMSColumnsCarousel"]_.CMSCardContentWrapper]:p-0',

                    //Add text color for parent level cms column carousel(icon) - background will always be white
                    // (e.g Ideal for working from home, Suitable for online gaming with no lag)
                    '[&[data-component="CMSColumnsCarousel"]_[data-component="CMSCard"]]:text-black',

                    //CMSColumnsStandard  Add margin bottom if it doesn't have either CMSPlanCardBroadband or TrustPilotWidget
                    //CMSColumnsStandard  (e.g Manage your service in the app section)
                    `[&[data-component="CMSColumnsStandard"]:not(:has([data-component="CMSPlanCardBroadband"],[data-component="TrustPilotWidget"]))]:mb-8 
                   md:[&[data-component="CMSColumnsStandard"]:not(:has([data-component="CMSPlanCardBroadband"],[data-component="TrustPilotWidget"]))]:mb-12 
                   lg:[&[data-component="CMSColumnsStandard"]:not(:has([data-component="CMSPlanCardBroadband"],[data-component="TrustPilotWidget"]))]:mb-20`,

                    //Add text color for h1, h2 and h3
                    `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h1]:text-black 
                 [&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2]:text-black 
                 [&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h3]:text-black`,

                    //Add text deep teal for p tag inside columns
                    '[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:text-black',

                    // Add margin bottom for p tag
                    '[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:mb-4',

                    //Set CMSTextAdvanced component to w-full for CMSColumnsStandard
                    '[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]]:w-full',
                    //Remove margin top if it's first child for ul
                    'cms-render [&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_ul:first-child]:mt-0'
                  )}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'banner' } as const, (bannerData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSBanner
                  startingPosition={index}
                  className={cn(
                    'cms-render',

                    // Add margin top to h3 tag itself if it is not first child
                    `[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-8 
                      md:[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-12 
                      lg:[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-20`,

                    // Add margin top to h3 tag itself if it is not first child
                    `[&_[data-component="CMSText"]_h3:not(:first-child)]:mt-6 
                      md:[&_[data-component="CMSText"]_h3:not(:first-child)]:mt-8`,

                    // add margin top if it's not the first child
                    // TODO: CHECK IF NEEDED
                    // '[&:not(:first-child)]:mt-8 md:[&:not(:first-child)]:mt-12 lg:md:[&:not(:first-child)]:mt-20',

                    // set min height to 100vh if it's page is /app/download and there are no other cms components
                    {
                      'min-h-screen':
                        data.content.length === 1 && pageKey === 'download',
                    }
                  )}
                  bannerData={bannerData}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'card' } as const, (cardData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSCard cardData={cardData} className="container" />
              </ErrorBoundary>
            ))
            .with({ type: 'plan_cards' } as const, (planCardsData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSPlanCardsWrapper
                  planCardsData={planCardsData}
                  className="container mb-6 md:mb-8 lg:mb-16"
                />
              </ErrorBoundary>
            ))
            .with({ type: 'tabs' } as const, (tabsData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSTabs tabsData={tabsData} className="container cms-render" />
              </ErrorBoundary>
            ))
            .with({ type: 'text' } as const, (textData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSText
                  textData={textData}
                  className={cn(
                    'container',

                    //Add color for h1 top level CMSText
                    '[&_h1]:text-black',
                    //Add color for h2 top level CMSText
                    '[&_h2]:text-black',
                    //Add color for h3 top level CMSText
                    '[&_h3]:text-black',

                    //Add margin bottom for h1 top level CMSText if it has h1
                    '[&:has(h1)]:mb-6 md:[&:has(h1)]:mb-8 lg:[&:has(h1)]:mb-16',

                    //Add margin bottom for h2 top level CMSText if it has h2
                    '[&:has(h2)]:mb-6 md:[&:has(h2)]:mb-8 lg:[&:has(h2)]:mb-16',

                    //Add margin bottom for h3 top level CMSText if it has h3
                    '[&:has(h3)]:mb-6 md:[&:has(h3)]:mb-8 lg:[&:has(h3)]:mb-16',
                    //Add margin top to h2 tag itself
                    '[&_h1]:mt-8 md:[&_h1]:mt-12 lg:[&_h1]:mt-20',

                    //Add margin top to h2 tag itself
                    '[&_h2]:mt-8 md:[&_h2]:mt-12 lg:[&_h2]:mt-20',

                    //Add margin top to h3 tag itself
                    '[&_h3]:mt-6 md:[&_h3]:mt-8',

                    //Add font styles for p tag that has no a tag children top level CMS text
                    '[&_p]:text-black',
                    //Add margin top for p tag that has no a tag children top level CMS text
                    '[&_p]:mt-2 md:[&_p]:mt-4',

                    // Add margin bottom for p tag
                    '[&_p]:mb-4',
                    'cms-render'
                  )}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'text_advanced' } as const, (textAdvancedData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSTextAdvanced
                  textAdvancedData={textAdvancedData}
                  className="container"
                />
              </ErrorBoundary>
            ))
            .with({ type: 'call_to_action' } as const, (data) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSCTAButton
                  ctaData={data}
                  // Add container
                  className={cn('container')}
                  // Font size for tablet and desktop breakpoint
                  buttonClassName={cn('md:h-[52px]')}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'image' } as const, (data) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSImage
                  className={cn('container', 'my-8 md:my-12')}
                  imageData={data}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'video' } as const, (data) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <Suspense fallback={null}>
                  <LazyCMSVideo
                    data={data}
                    className={cn(
                      'container rounded-[15px]',
                      // Add height for video component
                      'h-[163px] md:h-[398px] lg:h-[506px]',
                      // Add margin top and bottom for video component
                      'my-8 md:my-12'
                    )}
                  />
                </Suspense>
              </ErrorBoundary>
            ))
            .with({ type: 'placeholder' } as const, (data) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />} key={index}>
                <CMSPlaceholder placeholderData={data} />
              </ErrorBoundary>
            ))
            .otherwise(() => (
              <div key={index} className="container">
                Unhandled CMSComponent
              </div>
            ))
        )}
      </div>
    </>
  );
};

export { CMSRender };
